import { useAnalytics } from "../lib/analytics";
import type { Question } from "../types/onboarding";

/**
 * Centralized hook for question-level analytics tracking
 */
export function useQuestionAnalytics(question: Question) {
	const { logEvent } = useAnalytics();

	const trackSelection = (
		selectedValue: string,
		allSelected?: string[],
		eventPrefix = "option",
	) => {
		logEvent(`${question.type}_${eventPrefix}_selected`, undefined, {
			field_name: question.field,
			question_type: question.type,
			selected_value: selectedValue,
			all_selected: allSelected ? allSelected.join(",") : selectedValue,
			total_selected: allSelected ? allSelected.length.toString() : "1",
		});
	};

	const trackDeselection = (
		deselectedValue: string,
		remainingSelected?: string[],
		eventPrefix = "option",
	) => {
		logEvent(`${question.type}_${eventPrefix}_deselected`, undefined, {
			field_name: question.field,
			question_type: question.type,
			deselected_value: deselectedValue,
			remaining_selected: remainingSelected ? remainingSelected.join(",") : "",
			remaining_count: remainingSelected
				? remainingSelected.length.toString()
				: "0",
		});
	};

	const trackTextInput = (inputLength: number, eventPrefix = "input") => {
		logEvent(`${question.type}_${eventPrefix}_entered`, undefined, {
			field_name: question.field,
			question_type: question.type,
			input_length: inputLength.toString(),
			has_content: (inputLength > 0).toString(),
		});
	};

	const trackDropdownOpen = (eventPrefix = "dropdown") => {
		logEvent(`${question.type}_${eventPrefix}_opened`, undefined, {
			field_name: question.field,
			question_type: question.type,
		});
	};

	const trackDropdownClose = (eventPrefix = "dropdown") => {
		logEvent(`${question.type}_${eventPrefix}_closed`, undefined, {
			field_name: question.field,
			question_type: question.type,
		});
	};

	const trackValidationError = (
		missingFields: string[],
		errorType = "validation_error",
	) => {
		logEvent(`${question.type}_${errorType}`, undefined, {
			field_name: question.field,
			question_type: question.type,
			missing_fields: missingFields.join(","),
			missing_count: missingFields.length.toString(),
		});
	};

	const trackCustomSelection = (
		eventName: string,
		metadata: Record<string, string> = {},
	) => {
		logEvent(`${question.type}_${eventName}`, undefined, {
			field_name: question.field,
			question_type: question.type,
			...metadata,
		});
	};

	return {
		trackSelection,
		trackDeselection,
		trackTextInput,
		trackDropdownOpen,
		trackDropdownClose,
		trackValidationError,
		trackCustomSelection,
	};
}
